import type { SubmitHandler } from 'react-hook-form';
import { faSignalStream } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiCreateLiveStreamResponseBody } from '~/lib/api/modules/LiveStreams';
import type { IUseCreateLiveStreamMutationValues } from '~/lib/api/modules/LiveStreams/hooks/useCreateLiveStreamMutation';
import type { ILiveStreamFormValues } from './LiveStreamForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useCreateLiveStreamMutation } from '~/lib/api/modules/LiveStreams/hooks/useCreateLiveStreamMutation';
import { LiveStreamForm } from './LiveStreamForm';

export type ICreateLiveStreamFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiCreateLiveStreamResponseBody,
    inputValues: IUseCreateLiveStreamMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
};

export const CreateLiveStreamForm: React.FC<ICreateLiveStreamFormProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, appId } = props;
  const createLiveStreamMutation = useCreateLiveStreamMutation();
  const { t } = useTranslation();

  const handleSubmit: SubmitHandler<ILiveStreamFormValues> = (values) => {
    if (createLiveStreamMutation.isLoading) {
      return;
    }

    return createLiveStreamMutation.mutateAsync(
      {
        appId,
        body: {
          name: values.name!,
          startDateTime: values.startDateTime!,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx} draftOutlineSize="lg">
      <ContentBlock.Title icon={<FontAwesomeIcon icon={faSignalStream} />}>
        {t('CreateLiveStreamForm.title')}
      </ContentBlock.Title>

      <LiveStreamForm
        mode="creation"
        defaultValues={{
          startDateTime: new Date().toISOString(),
        }}
        cancelUrl={cancelUrl}
        onSubmit={handleSubmit}
      />
    </ContentBlock>
  );
};
